import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { UnwrappedProfile } from "./UnwrappedProfile";
import { useTunnelCenterline } from "./useTunnelCenterline";
import { useRoute } from "./useRoute";
import { Dashboard } from "./Dashboard";
import { MapCollection } from "./MapCollection";
import { TunnelLineView } from "./TunnelLineView";
import { TerrainModelsView } from "./TerrainModelsView";
import { GeoModelView } from "./GeoModelView";
import { ProjectProvider, useProject } from "./ProjectContext";
import { getProject, PROJECTS } from "./projectConfig";
import { AppToolbarProvider } from "./WhatsNew";

/**
 * Parse a route like "/<projectId>/tunnel" into { projectId, view }.
 * Returns null for routes that don't match the /<projectId>/<view> pattern.
 */
function parseRoute(route: string): { projectId: string; view: string } | null {
  const parts = route.split("/").filter(Boolean); // e.g. ["e16-fas", "tunnel"]
  if (parts.length < 2) return null;
  const [projectId, view] = parts;
  if (!PROJECTS[projectId]) return null;
  return { projectId, view };
}

/**
 * Wrapper that loads tunnel data only when a viewer route is active.
 * This keeps the dashboard lightweight — no fetches until needed.
 */
function TunnelViewer({ view, navigate }: { view: string; navigate: (r: string) => void }) {
  const project = useProject();
  const data = useTunnelCenterline();

  if (view === "utbrettsprofil" && data) {
    return (
      <UnwrappedProfile
        data={data}
        profileAssignments={project.profileAssignments}
        onBack={() => navigate("/")}
      />
    );
  }

  return (
    <App
      data={data}
      profileAssignments={project.profileAssignments}
      onNavigateUtbrettsprofil={() => navigate("utbrettsprofil")}
      onNavigateDashboard={() => navigate("/")}
    />
  );
}

function Root() {
  const [route, navigate] = useRoute();

  // Dashboard is the default landing page
  if (route === "/") {
    return <Dashboard onNavigate={navigate} />;
  }

  // Parse /<projectId>/<view> routes
  const parsed = parseRoute(route);
  if (!parsed) {
    return <Dashboard onNavigate={navigate} />;
  }

  const project = getProject(parsed.projectId);

  /** Navigate within the current project, or to "/" for dashboard */
  const projectNavigate = (target: string) => {
    if (target === "/") return navigate(`/?project=${parsed.projectId}`);
    return navigate(`/${parsed.projectId}/${target}`);
  };

  const backToDashboard = () => navigate(`/?project=${parsed.projectId}`);

  return (
    <ProjectProvider value={project}>
      {parsed.view === "map" && (
        <MapCollection onBack={backToDashboard} />
      )}
      {parsed.view === "tunnel-line" && (
        <TunnelLineView onBack={backToDashboard} />
      )}
      {parsed.view === "terrain" && (
        <TerrainModelsView onBack={backToDashboard} />
      )}
      {parsed.view === "geo-model" && (
        <GeoModelView onBack={backToDashboard} onNavigate={projectNavigate} />
      )}
      {(parsed.view === "tunnel" || parsed.view === "utbrettsprofil") && (
        <TunnelViewer view={parsed.view} navigate={projectNavigate} />
      )}
      {parsed.view !== "map" && parsed.view !== "tunnel" && parsed.view !== "utbrettsprofil" && parsed.view !== "tunnel-line" && parsed.view !== "terrain" && parsed.view !== "geo-model" && (
        <Dashboard onNavigate={navigate} />
      )}
    </ProjectProvider>
  );
}

function AuthGate({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<"loading" | "ok" | "forbidden">("loading");
  const [userName, setUserName] = useState<string>("");

  useEffect(() => {
    fetch("/auth/me")
      .then(async (r) => {
        if (!r.ok) {
          window.location.href = "/auth/login?return_to=" + encodeURIComponent(window.location.pathname + window.location.search);
          return;
        }
        const data = await r.json();
        const roles: string[] = data.user?.roles || [];
        if (roles.includes("viewer")) {
          setState("ok");
        } else {
          setUserName(data.user?.name || data.user?.preferred_username || data.user?.email || "");
          setState("forbidden");
        }
      })
      .catch(() => {
        window.location.href = "/auth/login?return_to=" + encodeURIComponent(window.location.pathname + window.location.search);
      });
  }, []);

  if (state === "loading") return null;
  if (state === "forbidden") return <AccessDenied userName={userName} />;
  return <>{children}</>;
}

function AccessDenied({ userName }: { userName: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "#1a1a2e", color: "#e0e0e0", fontFamily: "system-ui, sans-serif" }}>
      <div style={{ textAlign: "center", maxWidth: 480, padding: 32 }}>
        <h1 style={{ fontSize: 48, margin: "0 0 16px", color: "#ff6b6b" }}>Access Denied</h1>
        <p style={{ fontSize: 18, lineHeight: 1.6, margin: "0 0 24px" }}>
          {userName ? `Hi ${userName}, you` : "You"} don't have the required <strong>viewer</strong> role to access this application.
        </p>
        <p style={{ fontSize: 14, color: "#888", margin: "0 0 32px" }}>
          Contact your administrator to request access.
        </p>
        <a href="/auth/logout" style={{ display: "inline-block", padding: "10px 24px", background: "#4a4a6a", color: "#fff", borderRadius: 6, textDecoration: "none", fontSize: 14 }}>
          Sign out
        </a>
      </div>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <AuthGate>
      <AppToolbarProvider>
        <Root />
      </AppToolbarProvider>
    </AuthGate>
  </StrictMode>
);
